import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Tabs, message, Button, DatePicker, Space, Popconfirm } from "antd";
import { Card } from "react-bootstrap";
import FeatherIcon from "feather-icons-react";
import AgGridWithColumnState from "../components/AgGridWithColumnState";
import dayjs from "dayjs";
import Header from "../../InitialPage/Sidebar/Header";
import {
  getTodayActions,
  getActionHistoryByMonth,
  completeAction,
  getNotificationCount,
  clearAllTodayActions,
} from "../../core/api/actions";

const ActionsPage = () => {
  const [todayActions, setTodayActions] = useState([]);
  const [historyActions, setHistoryActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [processingActions, setProcessingActions] = useState({});
  const [clearingAll, setClearingAll] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());

  const historyGridRef = useRef(null);

  // Extract invoice/bill number from title (e.g., "Bill Approved by Senior Accountant - 3211A" -> "3211A")
  const extractNumberFromTitle = (title) => {
    if (!title) return null;
    // Try to extract the number after the last dash
    const match = title.match(/[-–]\s*([A-Za-z0-9-]+)\s*$/);
    if (match) return match[1];
    return null;
  };

  // Extract approver from title (e.g., "Bill Approved by Senior Accountant - 3211A" -> "Senior Accountant")
  const extractApproverFromTitle = (title) => {
    if (!title) return null;
    // Pattern: "Approved by [ROLE] - [NUMBER]" or "Approved by [ROLE]"
    const match = title.match(/Approved by\s+([^-–]+?)(?:\s*[-–]|$)/i);
    if (match) return match[1].trim();
    return null;
  };

  // Fetch today's actions
  const fetchTodayActions = useCallback(async () => {
    try {
      setLoading(true);
      const todayData = await getTodayActions();
      const todayDataArray = Array.isArray(todayData)
        ? todayData
        : todayData.results || [];

      const formattedTodayActions = todayDataArray.map((action) => {
        // Try to get invoice number from action_data first, then extract from title
        const invoiceNum =
          action.action_data?.invoice_number ||
          action.action_data?.bill_number ||
          action.action_data?.credit_number ||
          action.action_data?.number ||
          extractNumberFromTitle(action.title);

        // Try to get approver from action_data first, then extract from title
        const approver =
          action.completed_by_name ||
          action.action_data?.approved_by ||
          action.action_data?.approver ||
          extractApproverFromTitle(action.title);

        // Get who created the invoice/bill/credit
        const createdBy =
          action.action_data?.created_by ||
          action.action_data?.creator ||
          action.action_data?.created_by_name ||
          action.created_by_name ||
          "-";

        // Get approver's email
        const approverEmail =
          action.completed_by_email ||
          action.action_data?.approved_by_email ||
          action.action_data?.approver_email ||
          "-";

        return {
          id: action.id,
          title: action.title,
          description: action.description || "",
          priority: action.priority,
          dueDate: action.due_date,
          type: action.action_type,
          status: action.status,
          mediaPlanId: action.media_plan,
          actionData: action.action_data || {},
          invoiceNumber: invoiceNum || "-",
          approvedBy: approver || "-",
          approverEmail: approverEmail,
          createdBy: createdBy,
          createdAt: action.created_at,
          completedAt: action.completed_at,
        };
      });

      setTodayActions(formattedTodayActions);
    } catch (err) {
      console.error("Failed to load today actions", err);
      message.error("Failed to load today's actions");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch history actions with month filter
  const fetchHistoryActions = useCallback(async (month = null) => {
    try {
      setHistoryLoading(true);
      const monthStr = month ? month.format("YYYY-MM") : null;
      const historyData = await getActionHistoryByMonth(monthStr);
      const historyDataArray = Array.isArray(historyData)
        ? historyData
        : historyData.results || [];

      const formattedHistoryActions = historyDataArray.map((action) => {
        // Try to get invoice number from action_data first, then extract from title
        const invoiceNum =
          action.action_data?.invoice_number ||
          action.action_data?.bill_number ||
          action.action_data?.credit_number ||
          action.action_data?.number ||
          extractNumberFromTitle(action.title);

        // Try to get approver from action_data first, then extract from title
        const approver =
          action.completed_by_name ||
          action.action_data?.approved_by ||
          action.action_data?.approver ||
          extractApproverFromTitle(action.title);

        // Get who created the invoice/bill/credit
        const createdBy =
          action.action_data?.created_by ||
          action.action_data?.creator ||
          action.action_data?.created_by_name ||
          action.created_by_name ||
          "-";

        // Get approver's email
        const approverEmail =
          action.completed_by_email ||
          action.action_data?.approved_by_email ||
          action.action_data?.approver_email ||
          "-";

        return {
          id: action.id,
          title: action.title,
          description: action.description || "",
          priority: action.priority,
          dueDate: action.due_date,
          type: action.action_type,
          status: action.status,
          mediaPlanId: action.media_plan,
          actionData: action.action_data || {},
          invoiceNumber: invoiceNum || "-",
          approvedBy: approver || "-",
          approverEmail: approverEmail,
          createdBy: createdBy,
          createdAt: action.created_at,
          completedAt: action.completed_at,
        };
      });

      setHistoryActions(formattedHistoryActions);
    } catch (err) {
      console.error("Failed to load history actions", err);
      message.error("Failed to load action history");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTodayActions();
    fetchHistoryActions(selectedMonth);
  }, [fetchTodayActions, fetchHistoryActions, selectedMonth]);

  // Handle month change
  const handleMonthChange = (date) => {
    setSelectedMonth(date);
    if (date) {
      fetchHistoryActions(date);
    }
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    const newMonth = selectedMonth.subtract(1, "month");
    setSelectedMonth(newMonth);
    fetchHistoryActions(newMonth);
  };

  // Navigate to next month
  const goToNextMonth = () => {
    const newMonth = selectedMonth.add(1, "month");
    setSelectedMonth(newMonth);
    fetchHistoryActions(newMonth);
  };

  // Clear all today's actions
  const handleClearAll = async () => {
    if (todayActions.length === 0) {
      message.info("No actions to clear");
      return;
    }

    try {
      setClearingAll(true);

      // Complete all actions one by one
      const completionPromises = todayActions.map((action) =>
        completeAction(action.id).catch((err) => {
          console.error(`Failed to complete action ${action.id}:`, err);
          return null;
        })
      );

      await Promise.all(completionPromises);

      // Move all to history
      const completedActions = todayActions.map((action) => ({
        ...action,
        completedAt: new Date().toISOString(),
        status: "completed",
      }));

      setHistoryActions([...completedActions, ...historyActions]);
      setTodayActions([]);

      message.success(`Cleared ${completionPromises.length} notifications`);

      // Refresh notification count
      try {
        const count = await getNotificationCount();
        window.dispatchEvent(
          new CustomEvent("notificationCountUpdated", { detail: { count } })
        );
      } catch (err) {
        console.error("Error refreshing notification count:", err);
      }
    } catch (error) {
      console.error("Error clearing all actions:", error);
      message.error("Failed to clear all actions");
    } finally {
      setClearingAll(false);
    }
  };

  // Handle completing a single action
  const handleCompleteAction = async (id) => {
    try {
      setProcessingActions((prev) => ({ ...prev, [id]: "completing" }));

      const action = todayActions.find((a) => a.id === id);
      await completeAction(id);

      // Remove from today's actions
      const updatedActions = todayActions.filter((a) => a.id !== id);
      setTodayActions(updatedActions);

      // Add to history
      if (action) {
        const historyAction = {
          ...action,
          completedAt: new Date().toISOString(),
          status: "completed",
        };
        setHistoryActions([historyAction, ...historyActions]);
      }

      message.success("Notification dismissed");

      // Refresh notification count
      try {
        const count = await getNotificationCount();
        window.dispatchEvent(
          new CustomEvent("notificationCountUpdated", { detail: { count } })
        );
      } catch (err) {
        console.error("Error refreshing notification count:", err);
      }
    } catch (error) {
      console.error("Error completing action:", error);
      message.error("Failed to dismiss notification");
    } finally {
      setProcessingActions((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    }
  };

  // Function to render priority badge
  const renderPriorityBadge = (priority) => {
    const badgeClass =
      priority === "high"
        ? "bg-danger"
        : priority === "medium"
        ? "bg-warning"
        : "bg-info";
    return <span className={`badge ${badgeClass} me-2`}>{priority}</span>;
  };

  // Function to render action type icon
  const renderTypeIcon = (type) => {
    switch (type) {
      case "invoice_approval":
      case "bill_approval":
      case "credit_approval":
        return (
          <FeatherIcon icon="check-circle" className="text-primary me-2" />
        );
      case "approval":
        return (
          <FeatherIcon icon="check-circle" className="text-primary me-2" />
        );
      case "alert":
        return (
          <FeatherIcon icon="alert-triangle" className="text-warning me-2" />
        );
      case "task":
        return <FeatherIcon icon="clipboard" className="text-info me-2" />;
      case "notification":
        return <FeatherIcon icon="bell" className="text-secondary me-2" />;
      default:
        return <FeatherIcon icon="circle" className="text-secondary me-2" />;
    }
  };

  // AG-Grid column definitions for history
  const historyColumnDefs = useMemo(
    () => [
      {
        headerName: "Invoice/Bill #",
        field: "invoiceNumber",
        sortable: true,
        filter: true,
        width: 140,
        cellRenderer: (params) => {
          const value = params.value || "-";
          return (
            <span style={{ fontWeight: 500, color: "#1890ff" }}>{value}</span>
          );
        },
      },
      {
        headerName: "Type",
        field: "type",
        sortable: true,
        filter: true,
        width: 150,
        cellRenderer: (params) => {
          const typeLabels = {
            invoice_approval: "Invoice Approval",
            bill_approval: "Bill Approval",
            credit_approval: "Credit Approval",
            approval: "Approval",
            alert: "Alert",
            task: "Task",
            notification: "Notification",
          };
          const label = typeLabels[params.value] || params.value || "-";
          const badgeClass = params.value?.includes("approval")
            ? "bg-primary"
            : params.value === "alert"
            ? "bg-warning"
            : params.value === "task"
            ? "bg-info"
            : "bg-secondary";
          return <span className={`badge ${badgeClass}`}>{label}</span>;
        },
      },
      {
        headerName: "Title",
        field: "title",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 200,
      },
      {
        headerName: "Description",
        field: "description",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 200,
        cellStyle: { color: "#666" },
      },
      {
        headerName: "Date",
        field: "dueDate",
        sortable: true,
        filter: "agDateColumnFilter",
        width: 120,
        valueFormatter: (params) => {
          if (!params.value) return "-";
          return dayjs(params.value).format("MMM DD, YYYY");
        },
      },
      {
        headerName: "Approved By",
        field: "approvedBy",
        sortable: true,
        filter: true,
        width: 220,
        cellRenderer: (params) => {
          const name = params.value || "-";
          const email = params.data?.approverEmail;
          // Show name with email if available (like "Senior Accountant by email@example.com")
          if (name !== "-" && email && email !== "-") {
            return (
              <span style={{ fontWeight: 500 }}>
                {name} by {email}
              </span>
            );
          }
          return <span style={{ fontWeight: 500 }}>{name}</span>;
        },
      },
      {
        headerName: "Approver Email",
        field: "approverEmail",
        sortable: true,
        filter: true,
        width: 200,
        cellRenderer: (params) => {
          const value = params.value || "-";
          if (value === "-") return value;
          return (
            <a
              href={`mailto:${value}`}
              style={{ color: "#1890ff", textDecoration: "none" }}
            >
              {value}
            </a>
          );
        },
      },
      {
        headerName: "Created By",
        field: "createdBy",
        sortable: true,
        filter: true,
        width: 150,
        cellRenderer: (params) => {
          const value = params.value || "-";
          return <span style={{ fontWeight: 500 }}>{value}</span>;
        },
      },
      {
        headerName: "Priority",
        field: "priority",
        sortable: true,
        filter: true,
        width: 100,
        cellRenderer: (params) => {
          const priority = params.value || "low";
          const badgeClass =
            priority === "high"
              ? "bg-danger"
              : priority === "medium"
              ? "bg-warning"
              : "bg-info";
          return <span className={`badge ${badgeClass}`}>{priority}</span>;
        },
      },
      {
        headerName: "Status",
        field: "status",
        sortable: true,
        filter: true,
        width: 110,
        cellRenderer: (params) => {
          const status = params.value || "pending";
          const badgeClass =
            status === "completed"
              ? "bg-success"
              : status === "cancelled"
              ? "bg-secondary"
              : "bg-warning";
          return <span className={`badge ${badgeClass}`}>{status}</span>;
        },
      },
      {
        headerName: "Completed At",
        field: "completedAt",
        sortable: true,
        filter: "agDateColumnFilter",
        width: 160,
        valueFormatter: (params) => {
          if (!params.value) return "-";
          return dayjs(params.value).format("MMM DD, YYYY HH:mm");
        },
      },
    ],
    []
  );

  // AG-Grid default column definition
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      suppressMovable: false,
    }),
    []
  );

  // Grid ready handler
  const onGridReady = useCallback((params) => {
    params.api.sizeColumnsToFit();
  }, []);

  // Render today's actions
  const renderTodayActions = () => {
    if (loading) {
      return <div className="text-center p-5">Loading...</div>;
    }

    return (
      <>
        {/* Clear All Button */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="text-muted">
            {todayActions.length} notification
            {todayActions.length !== 1 ? "s" : ""} pending
          </span>
          {todayActions.length > 0 && (
            <Popconfirm
              title="Clear all notifications?"
              description="This will dismiss all current notifications."
              onConfirm={handleClearAll}
              okText="Yes, Clear All"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="primary"
                danger
                icon={<FeatherIcon icon="trash-2" size={14} />}
                loading={clearingAll}
              >
                Clear All ({todayActions.length})
              </Button>
            </Popconfirm>
          )}
        </div>

        {todayActions.length === 0 ? (
          <div className="text-center p-5">
            <FeatherIcon
              icon="check-circle"
              size={48}
              className="text-success mb-3"
            />
            <h4>All caught up!</h4>
            <p className="text-muted">You have no pending actions for today.</p>
          </div>
        ) : (
          <div className="actions-list">
            {todayActions.map((action) => {
              const isApprovalAction =
                action.type === "invoice_approval" ||
                action.type === "bill_approval" ||
                action.type === "credit_approval";
              return (
                <Card
                  key={action.id}
                  className="mb-3"
                  style={
                    isApprovalAction && action.status === "pending"
                      ? { borderLeft: "4px solid #1890ff" }
                      : {}
                  }
                >
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center flex-grow-1">
                        {renderTypeIcon(action.type)}
                        <div className="flex-grow-1">
                          <h5 className="mb-1">{action.title}</h5>
                          <p className="text-muted mb-0">
                            {action.description}
                          </p>
                          {action.invoiceNumber &&
                            action.invoiceNumber !== "-" && (
                              <small className="text-primary">
                                <strong>#{action.invoiceNumber}</strong>
                              </small>
                            )}
                        </div>
                      </div>
                      <div className="text-end">
                        {renderPriorityBadge(action.priority)}
                        {isApprovalAction && action.status === "pending" && (
                          <span className="badge bg-primary ms-2">New</span>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 text-end">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleCompleteAction(action.id)}
                        disabled={processingActions[action.id]}
                        title="Dismiss this notification"
                      >
                        {processingActions[action.id] === "completing" ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Dismissing...
                          </>
                        ) : (
                          <>
                            <FeatherIcon icon="x" size={14} /> Dismiss
                          </>
                        )}
                      </button>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </>
    );
  };

  // Render action history with AG-Grid
  const renderActionHistory = () => {
    return (
      <>
        {/* Month Navigation and Filter */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Space>
            <Button
              icon={<FeatherIcon icon="chevron-left" size={16} />}
              onClick={goToPreviousMonth}
            />
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={handleMonthChange}
              format="MMMM YYYY"
              allowClear={false}
              style={{ width: 180 }}
            />
            <Button
              icon={<FeatherIcon icon="chevron-right" size={16} />}
              onClick={goToNextMonth}
              disabled={selectedMonth.isSame(dayjs(), "month")}
            />
          </Space>
          <span className="text-muted">
            {historyActions.length} record
            {historyActions.length !== 1 ? "s" : ""} found
          </span>
        </div>

        {/* AG-Grid */}
        <div
          className="ag-theme-alpine"
          style={{
            height: "calc(100vh - 350px)",
            minHeight: 400,
            width: "100%",
          }}
        >
          <AgGridWithColumnState
            tableId="actions-history"
            gridApiRef={historyGridRef}
            rowData={historyActions}
            columnDefs={historyColumnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={50}
            paginationPageSizeSelector={[25, 50, 100, 200]}
            animateRows={true}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            enableCellTextSelection={true}
            loading={historyLoading}
            overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading history...</span>'
            overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No actions found for this month</span>'
            getRowId={(params) => String(params.data.id)}
          />
        </div>
      </>
    );
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <Header title="Actions" />
          <div className="card">
            <div className="card-body">
              <Tabs
                defaultActiveKey="1"
                items={[
                  {
                    key: "1",
                    label: (
                      <span>
                        <FeatherIcon
                          icon="calendar"
                          className="me-2"
                          size={16}
                        />
                        Today&apos;s Actions
                        {todayActions.length > 0 && (
                          <span className="badge rounded-pill bg-danger ms-2">
                            {todayActions.length}
                          </span>
                        )}
                      </span>
                    ),
                    children: renderTodayActions(),
                  },
                  {
                    key: "2",
                    label: (
                      <span>
                        <FeatherIcon icon="clock" className="me-2" size={16} />
                        Actions History
                      </span>
                    ),
                    children: renderActionHistory(),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActionsPage;
